import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import {
  Machine, MachineType, MachineNotes, MachineDocumentation,
  MachinePhoto, MachineMaintenanceAction, RoleType, DocumentationListItem,
  Customer, Address,
} from '../types';
import {
  machineApi, machineTypeApi, notesApi,
  documentationApi, photoApi, maintenanceApi,
  customerApi, addressApi, DuplicateDocumentationInfo,
} from '../api/machines';
import TicketsTab from '../components/TicketsTab';
import { MessageEditor, Markdown } from '../components/RichText';
import Modal from '../components/Modal';
import DownloadButton from '../components/DownloadButton';
import { NavIcon, IconName } from '../components/icons';
import { documentationDownloadUrl } from '../utils/fileUrl';
import { Toast, useToast } from '../components/Toast';
import './MachineDetail.css';

type Tab = 'notes' | 'photos' | 'docs' | 'maintenance' | 'tickets';

const TABS: Tab[] = ['notes', 'photos', 'docs', 'maintenance', 'tickets'];
const isTab = (s: string | null): s is Tab => !!s && (TABS as string[]).includes(s);

// Mobilny selektor sekcji: ikona (spójna z sidebarem/bottom barem) + krótka etykieta,
// żeby 5 komórek zmieściło się w jednym rzędzie bez zawijania.
const TAB_ICON: Record<Tab, IconName> = {
  notes: 'notes',
  photos: 'image',
  docs: 'docs',
  maintenance: 'wrench',
  tickets: 'tickets',
};
const TAB_SHORT: Record<Tab, string> = {
  notes: 'Notatki',
  photos: 'Zdjęcia',
  docs: 'Dokum.',
  maintenance: 'Serwis',
  tickets: 'Zgłosz.',
};

const TAB_PAGE_SIZE = 5;

const roleBadge = (role: RoleType | null): { className: string; label: string } | null => {
  if (!role) return null;
  if (role === 'ADMIN') return { className: 'badge badge-admin', label: 'Admin' };
  if (role === 'CUSTOMER') return { className: 'badge badge-customer', label: 'Klient' };
  // EMPLOYEE_ADMIN i EMPLOYEE to obie role po stronie integratora → wspólna etykieta.
  return { className: 'badge badge-integrator', label: 'Integrator' };
};

const creatorTypeBadge = (role: RoleType | null): { className: string; label: string } | null => {
  if (!role) return null;
  if (role === 'CUSTOMER') return { className: 'badge badge-customer', label: 'Klient' };
  return { className: 'badge badge-integrator', label: 'Integrator' };
};

interface CreatorLineProps {
  firstName: string | null;
  lastName: string | null;
  role: RoleType | null;
  avatarUrl?: string | null;
}

const CreatorLine: React.FC<CreatorLineProps> = ({ firstName, lastName, role, avatarUrl }) => {
  const badge = creatorTypeBadge(role);
  if (!badge && !firstName && !avatarUrl) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
      {avatarUrl && (
        <img src={avatarUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
      )}
      {firstName && <span className="item-meta">{firstName} {lastName}</span>}
      {badge && <span className={badge.className}>{badge.label}</span>}
    </span>
  );
};

const Pager: React.FC<{ page: number; totalPages: number; onPage: (p: number) => void }> = ({ page, totalPages, onPage }) =>
  totalPages > 1 ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
      <button className="btn btn-secondary btn-sm" disabled={page <= 0} onClick={() => onPage(page - 1)}>Poprzednia</button>
      <span className="item-meta pagination-info">Strona {page + 1} z {totalPages}</span>
      <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)}>Następna</button>
    </div>
  ) : null;

const printMachineQr = (machineName: string, machineId: string, svgElementId: string) => {
  const win = window.open('', '_blank', 'width=600,height=720');
  if (!win) return;
  const svgEl = document.getElementById(svgElementId);
  const svgMarkup = svgEl ? svgEl.outerHTML : '';
  win.document.write(`<!DOCTYPE html><html><head><title>QR — ${machineName}</title>
<style>
  body { font-family: system-ui, sans-serif; margin: 0; padding: 24px; display: flex; flex-direction: column; align-items: center; gap: 12px; }
  h1 { font-size: 18px; margin: 0; text-align: center; }
  .meta { color: #555; font-size: 12px; }
  .frame { border: 2px solid #000; padding: 12px; border-radius: 8px; }
  @media print {
    @page { margin: 12mm; }
    body { padding: 0; }
  }
</style></head>
<body>
  <h1>${machineName}</h1>
  <div class="meta">ID ${machineId}</div>
  <div class="frame">${svgMarkup}</div>
  <div class="meta">Zeskanuj telefonem, aby zgłosić usterkę</div>
  <script>window.onload = () => { window.focus(); window.print(); };</script>
</body></html>`);
  win.document.close();
};

const MachineDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const machineId = id || '';
  const isCustomer = user?.roles?.includes('CUSTOMER');
  const isStaff = user?.roles?.includes('ADMIN') || user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('EMPLOYEE');
  // Ustawienia maszyny (m.in. system zgłoszeń) — admin integratora; super-admin też, jak wszędzie.
  const isIntegratorAdmin = user?.roles?.includes('EMPLOYEE_ADMIN') || user?.roles?.includes('ADMIN');

  const [machine, setMachine] = useState<Machine | null>(null);
  const [machineType, setMachineType] = useState<MachineType | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerAddress, setCustomerAddress] = useState<Address | null>(null);
  const initialTabParam = searchParams.get('tab');
  // Mobile: klient przychodzi po zgłoszenia (skan QR / status) — startujemy od nich.
  // Sprawdzane raz przy montowaniu; desktopowe domyślne zakładki bez zmian.
  const startedOnMobile = typeof window !== 'undefined'
    && window.matchMedia('(max-width: 767.98px)').matches;
  const [activeTab, setActiveTab] = useState<Tab>(
    isTab(initialTabParam) ? initialTabParam
      : isCustomer && startedOnMobile ? 'tickets'
      : (isStaff ? 'notes' : 'photos'));
  const [nameplateOpen, setNameplateOpen] = useState(false);
  const [notes, setNotes] = useState<MachineNotes[]>([]);
  const [docs, setDocs] = useState<MachineDocumentation[]>([]);
  const [photos, setPhotos] = useState<MachinePhoto[]>([]);
  const [maintenance, setMaintenance] = useState<MachineMaintenanceAction[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast, showToast, dismissToast } = useToast();

  // Pagination + multi-select for notes and maintenance actions
  const [notesPageNum, setNotesPageNum] = useState(0);
  const [notesTotalPages, setNotesTotalPages] = useState(1);
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(new Set());
  const [maintPageNum, setMaintPageNum] = useState(0);
  const [maintTotalPages, setMaintTotalPages] = useState(1);
  const [selectedMaintIds, setSelectedMaintIds] = useState<Set<string>>(new Set());
  const [confirmDelete, setConfirmDelete] = useState<null | 'notes' | 'maint'>(null);
  const [deleting, setDeleting] = useState(false);

  const loadMachine = useCallback(async () => {
    try {
      const { data } = await machineApi.getById(machineId);
      setMachine(data);

      // Typ maszyny jest niezależny od łańcucha klient→adres — puszczamy je równolegle,
      // zamiast 4 sekwencyjnych round-tripów przed pierwszym malowaniem strony.
      const typePromise = data.machineTypeId
        ? machineTypeApi.getAll().then((typeRes) =>
            setMachineType(typeRes.data.find((t) => t.id === data.machineTypeId) || null))
        : Promise.resolve();

      const customerPromise = data.ownerNip
        ? customerApi.getByNip(data.ownerNip).then(async (custRes) => {
            setCustomer(custRes.data);
            if (custRes.data?.addressId) {
              const addrRes = await addressApi.getById(custRes.data.addressId);
              setCustomerAddress(addrRes.data);
            }
          }).catch(() => {
            setCustomer(null);
            setCustomerAddress(null);
          })
        : Promise.resolve();

      await Promise.all([typePromise, customerPromise]);
    } catch {
      navigate('/machines');
    } finally {
      setLoading(false);
    }
  }, [machineId, navigate]);

  const loadNotes = useCallback(async (page: number) => {
    let { data } = await notesApi.getByMachineId(machineId, page, TAB_PAGE_SIZE);
    if (data.content.length === 0 && data.page > 0) {
      ({ data } = await notesApi.getByMachineId(machineId, data.page - 1, TAB_PAGE_SIZE));
    }
    setNotes(data.content);
    setNotesTotalPages(data.totalPages);
    setNotesPageNum(data.page);
    setSelectedNoteIds(new Set());
  }, [machineId]);

  const loadMaint = useCallback(async (page: number) => {
    let { data } = await maintenanceApi.getByMachineId(machineId, page, TAB_PAGE_SIZE);
    if (data.content.length === 0 && data.page > 0) {
      ({ data } = await maintenanceApi.getByMachineId(machineId, data.page - 1, TAB_PAGE_SIZE));
    }
    setMaintenance(data.content);
    setMaintTotalPages(data.totalPages);
    setMaintPageNum(data.page);
    setSelectedMaintIds(new Set());
  }, [machineId]);

  const loadTabData = useCallback(async () => {
    try {
      switch (activeTab) {
        case 'notes':
          await loadNotes(0);
          break;
        case 'docs':
          setDocs((await documentationApi.getByMachineId(machineId)).data);
          break;
        case 'photos':
          setPhotos((await photoApi.getByMachineId(machineId)).data);
          break;
        case 'maintenance':
          await loadMaint(0);
          break;
      }
    } catch (e) {
      console.error('Failed to load tab data', e);
    }
  }, [activeTab, machineId, loadNotes, loadMaint]);

  useEffect(() => { loadMachine(); }, [loadMachine]);
  useEffect(() => { loadTabData(); }, [activeTab, loadTabData]);
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (isTab(tabParam) && tabParam !== activeTab) setActiveTab(tabParam);
  }, [searchParams, activeTab]);
  // Zakładka „Zgłoszenia" istnieje tylko gdy maszyna ma włączony system zgłoszeń.
  useEffect(() => {
    if (machine && !machine.ticketSystemEnabled && activeTab === 'tickets') {
      setActiveTab(isStaff ? 'notes' : 'photos');
    }
    // Customers must not land on the internal notes tab (e.g. via ?tab=notes).
    if (!isStaff && activeTab === 'notes') {
      setActiveTab('photos');
    }
  }, [machine, activeTab, isStaff]);

  const switchTab = (next: Tab) => {
    setActiveTab(next);
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', next);
    setSearchParams(sp, { replace: true });
  };

  // Mobilny start od "tickets" mógł trafić na maszynę z wyłączonymi zgłoszeniami.
  useEffect(() => {
    if (machine && activeTab === 'tickets' && !machine.ticketSystemEnabled) {
      setActiveTab(isStaff ? 'notes' : 'photos');
    }
  }, [machine]); // eslint-disable-line react-hooks/exhaustive-deps


  const [newNote, setNewNote] = useState('');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const addNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      await notesApi.create(machineId, {
        description: newNote,
        date: new Date().toISOString(),
        createdByUserId: user!.userId,
      });
      setNewNote('');
      setShowNoteForm(false);
      loadTabData();
      showToast('success', 'Notatka dodana.');
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się dodać notatki.');
    } finally {
      setSavingNote(false);
    }
  };

  const [docFile, setDocFile] = useState<File | null>(null);
  const [docName, setDocName] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState('');
  const [docDuplicate, setDocDuplicate] = useState<DuplicateDocumentationInfo | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTickets, setSettingsTickets] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const isAdmin = user?.roles?.includes('ADMIN');

  // Super-admin only: while any note on the page is still being embedded, refresh
  // the current page (without touching selection/pagination) until indexing lands.
  const refreshNotesPage = useCallback(async () => {
    try {
      const { data } = await notesApi.getByMachineId(machineId, notesPageNum, TAB_PAGE_SIZE);
      setNotes(data.content);
      setNotesTotalPages(data.totalPages);
    } catch { /* ignore transient errors while polling */ }
  }, [machineId, notesPageNum]);

  const notesIndexing = !!isAdmin && notes.some((n) => n.embeddingPending);
  useEffect(() => {
    if (!notesIndexing) return;
    // Nie odpytujemy ukrytej karty; twardy limit ~2 min na wypadek, gdyby
    // indeksacja utknęła i embeddingPending nigdy nie zgasło.
    let polls = 0;
    const t = setInterval(() => {
      if (document.hidden) return;
      if (++polls > 48) { clearInterval(t); return; }
      refreshNotesPage();
    }, 2500);
    return () => clearInterval(t);
  }, [notesIndexing, refreshNotesPage]);

  /** Zaproszenie klienta do tej maszyny e-mailem (konto powstanie z zaproszenia albo user dostaje info). */
  const sendMachineInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteError('');
    setInviteSending(true);
    try {
      const { data } = await machineApi.inviteUser(machineId, email);
      showToast('success', data.message);
      setShowInviteModal(false);
      setInviteEmail('');
    } catch (e: any) {
      setInviteError(e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się wysłać zaproszenia.');
    } finally {
      setInviteSending(false);
    }
  };

  const openSettings = () => {
    setSettingsTickets(!!machine?.ticketSystemEnabled);
    setShowSettingsModal(true);
  };

  /** Zapis ustawień maszyny. Wyłączenie systemu zgłoszeń odbija się o 409, gdy są otwarte zgłoszenia. */
  const saveSettings = async () => {
    if (!machine || settingsTickets === machine.ticketSystemEnabled) {
      setShowSettingsModal(false);
      return;
    }
    setSettingsSaving(true);
    try {
      const { data } = await machineApi.setTicketSystem(machineId, settingsTickets);
      setMachine(data);
      showToast('success', data.ticketSystemEnabled
        ? 'System zgłoszeń został włączony dla tej maszyny.'
        : 'System zgłoszeń został wyłączony dla tej maszyny.');
      setShowSettingsModal(false);
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.response?.data?.detail
        || 'Nie udało się zapisać ustawień maszyny.');
    } finally {
      setSettingsSaving(false);
    }
  };

  /** Otwiera modal edycji z aktualnymi danymi maszyny. */
  const openEdit = () => {
    if (!machine) return;
    setEditName(machine.name);
    setEditDescription(machine.description || '');
    setShowEditModal(true);
  };

  /** Zapis tytułu i opisu maszyny — dostępny dla pracownika/admina integratora. */
  const saveEdit = async () => {
    if (!machine || !editName.trim()) return;
    setEditSaving(true);
    try {
      const { data } = await machineApi.update(machineId, {
        name: editName.trim(),
        description: editDescription.trim(),
        ownerNip: machine.ownerNip,
        machineTypeId: machine.machineTypeId,
        customerDepartmentId: machine.customerDepartmentId,
      });
      setMachine(data);
      setShowEditModal(false);
      showToast('success', 'Dane maszyny zostały zaktualizowane.');
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || e?.response?.data?.detail
        || 'Nie udało się zapisać danych maszyny.');
    } finally {
      setEditSaving(false);
    }
  };

  const resetDocForm = () => {
    setDocFile(null); setDocName(''); setDocDescription(''); setDocError(''); setDocDuplicate(null);
    const fi = document.getElementById('doc-file-input') as HTMLInputElement | null;
    if (fi) fi.value = '';
  };

  /** Podpięcie dokumentu zasugerowanego w 409 (identyczny plik już w bazie). */
  const linkDuplicateDoc = async () => {
    if (!docDuplicate) return;
    try {
      await documentationApi.link(machineId, docDuplicate.documentationId);
      showToast('success', `Podpięto istniejący dokument „${docDuplicate.name}”.`);
      resetDocForm();
      setShowDocForm(false);
      loadTabData();
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się podpiąć dokumentu.');
    }
  };

  const addDoc = async () => {
    setDocError('');
    setDocDuplicate(null);
    if (!docFile) return;
    if (!docName.trim()) {
      setDocError('Nazwa dokumentu jest wymagana');
      return;
    }
    setDocUploading(true);
    try {
      await documentationApi.upload(machineId, docFile, docName.trim(), docDescription, user!.userId);
      setDocFile(null);
      setDocName('');
      setDocDescription('');
      const fileInput = document.getElementById('doc-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      setShowDocForm(false);
      loadTabData();
      showToast('success', 'Dokument dodany.');
    } catch (e: any) {
      // 409 = identyczny plik już istnieje — pokazujemy sugestię podpięcia zamiast błędu.
      if (e?.response?.status === 409 && e.response.data?.documentationId) {
        setDocDuplicate(e.response.data as DuplicateDocumentationInfo);
        return;
      }
      console.error('Nie udało się dodać dokumentu', e);
      const msg = 'Nie udało się dodać dokumentu. Spróbuj ponownie.';
      setDocError(msg);
      showToast('error', msg);
    } finally {
      setDocUploading(false);
    }
  };

  const unlinkDoc = async (documentationId: string) => {
    await documentationApi.unlink(machineId, documentationId);
    loadTabData();
  };
  const deleteDoc = async (documentationId: string) => {
    if (!window.confirm('Usunąć dokumentację z całego systemu (ze wszystkich maszyn)?')) return;
    await documentationApi.delete(documentationId);
    loadTabData();
  };

  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoDescription, setPhotoDescription] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoProgress, setPhotoProgress] = useState<{ done: number; total: number } | null>(null);
  const [photoError, setPhotoError] = useState('');
  const [showPhotoForm, setShowPhotoForm] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [confirmDeletePhotoId, setConfirmDeletePhotoId] = useState<string | null>(null);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  useEffect(() => {
    if (photoIndex >= photos.length && photos.length > 0) setPhotoIndex(0);
  }, [photos, photoIndex]);

  const deletePhoto = async () => {
    if (!confirmDeletePhotoId) return;
    setDeletingPhoto(true);
    try {
      await photoApi.delete(confirmDeletePhotoId);
      setConfirmDeletePhotoId(null);
      await loadMachine();
      loadTabData();
      showToast('success', 'Zdjęcie usunięte.');
    } catch (e) {
      console.error('Nie udało się usunąć zdjęcia', e);
      showToast('error', 'Nie udało się usunąć zdjęcia. Spróbuj ponownie.');
    } finally {
      setDeletingPhoto(false);
    }
  };

  const resetPhotoForm = () => {
    setPhotoFiles([]);
    setPhotoDescription('');
    setPhotoError('');
    const fi = document.getElementById('photo-file-input') as HTMLInputElement | null;
    if (fi) fi.value = '';
  };

  const addPhoto = async () => {
    if (photoFiles.length === 0) return;
    setPhotoError('');
    setPhotoUploading(true);
    setPhotoProgress({ done: 0, total: photoFiles.length });
    const failed: string[] = [];
    let firstUploadedId: string | null = null;
    try {
      for (let i = 0; i < photoFiles.length; i++) {
        const f = photoFiles[i];
        try {
          const { data: uploaded } = await photoApi.upload(machineId, f, photoDescription, user!.userId);
          if (!firstUploadedId) firstUploadedId = uploaded.id;
        } catch {
          failed.push(f.name);
        }
        setPhotoProgress({ done: i + 1, total: photoFiles.length });
      }
      if (firstUploadedId && machine && !machine.thumbnailPhotoId) {
        try {
          const { data: updatedMachine } = await machineApi.setThumbnail(machineId, firstUploadedId);
          setMachine(updatedMachine);
        } catch (e) {
          // Ustawienie miniatury jest opcjonalne — nie przerywaj, jeśli się nie powiedzie
          console.error('Nie udało się ustawić miniatury', e);
        }
      }
      if (failed.length > 0) {
        setPhotoError(`Nie udało się wysłać: ${failed.join(', ')}`);
        showToast('error', `Nie udało się wysłać: ${failed.join(', ')}`);
      } else {
        resetPhotoForm();
        setShowPhotoForm(false);
        showToast('success', photoFiles.length > 1 ? 'Zdjęcia dodane.' : 'Zdjęcie dodane.');
      }
      loadTabData();
    } finally {
      setPhotoUploading(false);
      setPhotoProgress(null);
    }
  };

  const [maintForm, setMaintForm] = useState({ description: '' });
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [savingMaint, setSavingMaint] = useState(false);
  const addMaintenance = async () => {
    if (!maintForm.description.trim()) return;
    setSavingMaint(true);
    try {
      await maintenanceApi.create(machineId, {
        description: maintForm.description,
        date: new Date().toISOString(),
        createdByUserId: user!.userId,
      });
      setMaintForm({ description: '' });
      setShowMaintForm(false);
      loadTabData();
      showToast('success', 'Czynność serwisowa dodana.');
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się dodać czynności.');
    } finally {
      setSavingMaint(false);
    }
  };

  const toggleId = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) =>
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const runDelete = async () => {
    const type = confirmDelete;
    if (!type) return;
    const ids = Array.from(type === 'notes' ? selectedNoteIds : selectedMaintIds);
    if (ids.length === 0) { setConfirmDelete(null); return; }
    setDeleting(true);
    try {
      await Promise.all(ids.map((id) => (type === 'notes' ? notesApi.delete(id) : maintenanceApi.delete(id))));
      if (type === 'notes') await loadNotes(notesPageNum); else await loadMaint(maintPageNum);
      showToast('success', `Usunięto: ${ids.length}.`);
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się usunąć.');
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  if (loading) return <div className="empty-state">Ładowanie...</div>;
  if (!machine) return null;

  const tabs: { key: Tab; label: string }[] = [
    // Notes are an internal integrator tab — hidden from customers.
    ...(isStaff ? [{ key: 'notes' as Tab, label: 'Notatki' }] : []),
    { key: 'photos', label: 'Zdjęcia' },
    { key: 'docs', label: 'Dokumentacja' },
    { key: 'maintenance', label: 'Serwis' },
    ...(machine.ticketSystemEnabled ? [{ key: 'tickets' as Tab, label: 'Zgłoszenia' }] : []),
  ];

  const creatorBadge = roleBadge(machine.createdByRole);

  return (
    <div>
      <button className="btn btn-secondary btn-sm detail-back" onClick={() => navigate('/machines')}>
        &larr; Powrót
      </button>

      <div className="detail-header">
        <h1 className="page-title">{machine.name}</h1>
        {machineType && <span className="badge badge-primary">{machineType.name}</span>}
        {isStaff && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm"
              onClick={() => { setInviteError(''); setInviteEmail(''); setShowInviteModal(true); }}>
              Zaproś użytkownika
            </button>
            <button className="btn btn-secondary btn-sm" onClick={openEdit}>
              Edytuj
            </button>
            {isIntegratorAdmin && (
              <button className="btn btn-secondary btn-sm" onClick={openSettings}>
                Ustawienia
              </button>
            )}
          </div>
        )}
        <p className="detail-header-description">{machine.description || 'Brak opisu'}</p>
      </div>

      {/* Mobile: "tabliczka znamionowa" — zwarta identyfikacja maszyny zamiast
          hero-zdjęcia, karty Informacje i banneru klienta (te są ukrywane w CSS;
          desktop pozostaje nietknięty). */}
      <section className="machine-nameplate">
        <div className="nameplate-main">
          <div className="nameplate-photo">
            {machine.thumbnailUrl ? (
              <img src={machine.thumbnailUrl} alt={machine.name} />
            ) : (
              <NavIcon name="machines" size={24} />
            )}
          </div>
          <div className="nameplate-id">
            <h1 className="nameplate-name">{machine.name}</h1>
            <div className="nameplate-row">
              <span className="nameplate-key">Typ</span>
              <span className="nameplate-val">{machineType?.name || '—'}</span>
            </div>
            {isCustomer ? (
              <div className="nameplate-row">
                <span className="nameplate-key">Serwisuje</span>
                <span className="nameplate-val">{machine.integratorName || '—'}</span>
              </div>
            ) : (
              <div className="nameplate-row">
                <span className="nameplate-key">Klient</span>
                <span className="nameplate-val">
                  {customer?.companyName || `NIP ${machine.ownerNip}`}
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          className="nameplate-toggle"
          onClick={() => setNameplateOpen((open) => !open)}
          aria-expanded={nameplateOpen}
        >
          {nameplateOpen ? 'Zwiń szczegóły ▴' : 'Szczegóły ▾'}
        </button>
        {nameplateOpen && (
          <div className="nameplate-details">
            <div className="nameplate-row">
              <span className="nameplate-key">Opis</span>
              <span className="nameplate-val">{machine.description || 'Brak opisu'}</span>
            </div>
            {!isCustomer && customer?.companyName && (
              <div className="nameplate-row">
                <span className="nameplate-key">NIP</span>
                <span className="nameplate-val">{machine.ownerNip}</span>
              </div>
            )}
            {!isCustomer && customerAddress && (
              <div className="nameplate-row">
                <span className="nameplate-key">Adres</span>
                <span className="nameplate-val">
                  ul. {customerAddress.street} {customerAddress.buildingNumber}
                  {customerAddress.apartmentNumber && `/${customerAddress.apartmentNumber}`}
                  {', '}{customerAddress.postalCode} {customerAddress.city}
                </span>
              </div>
            )}
            <div className="nameplate-row">
              <span className="nameplate-key">Dodał</span>
              <span className="nameplate-val">
                {machine.createdByFirstName
                  ? `${machine.createdByFirstName} ${machine.createdByLastName}`
                  : '—'}
              </span>
            </div>
            <div className="nameplate-row">
              <span className="nameplate-key">Dodano</span>
              <span className="nameplate-val">
                {machine.createdAt
                  ? new Date(machine.createdAt).toLocaleDateString('pl-PL', { dateStyle: 'long' })
                  : '—'}
              </span>
            </div>
            {isStaff && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setInviteError(''); setInviteEmail(''); setShowInviteModal(true); }}>
                  Zaproś użytkownika
                </button>
                <button className="btn btn-secondary btn-sm" onClick={openEdit}>
                  Edytuj
                </button>
                {isIntegratorAdmin && (
                  <button className="btn btn-secondary btn-sm" onClick={openSettings}>
                    Ustawienia
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {showInviteModal && (
        <Modal title="Zaproś użytkownika do maszyny" dismissable={!inviteSending}
          onClose={() => setShowInviteModal(false)}>
          <p className="item-meta" style={{ marginBottom: '0.75rem' }}>
            Jeśli adres nie ma konta, wyślemy zaproszenie do portalu klienta
            (firma NIP {machine.ownerNip}). Jeśli konto istnieje i należy do tej firmy,
            użytkownik dostanie e-mail z informacją o maszynie.
          </p>
          {inviteError && <div className="auth-error">{inviteError}</div>}
          <div className="form-group">
            <label className="form-label">E-mail użytkownika</label>
            <input className="form-input" type="email" value={inviteEmail} autoFocus
              placeholder="np. jan.kowalski@firma.pl"
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendMachineInvite(); } }} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" disabled={inviteSending}
              onClick={() => setShowInviteModal(false)}>
              Anuluj
            </button>
            <button type="button" className="btn btn-primary"
              disabled={inviteSending || !inviteEmail.trim().includes('@')}
              onClick={sendMachineInvite}>
              {inviteSending ? 'Wysyłanie…' : 'Wyślij zaproszenie'}
            </button>
          </div>
        </Modal>
      )}

      {showSettingsModal && (
        <Modal title="Ustawienia maszyny" dismissable={!settingsSaving}
          onClose={() => setShowSettingsModal(false)}>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settingsTickets}
                disabled={settingsSaving}
                onChange={(e) => setSettingsTickets(e.target.checked)}
                style={{ marginTop: '0.2rem', flexShrink: 0 }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>System zgłoszeń dla klientów</span>
                <span className="item-meta" style={{ display: 'block' }}>
                  Klient widzi zakładkę „Zgłoszenia" i może zgłaszać usterki tej maszyny.
                  Wyłączenie jest możliwe dopiero, gdy wszystkie zgłoszenia są zamknięte.
                </span>
              </span>
            </label>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" disabled={settingsSaving}
              onClick={() => setShowSettingsModal(false)}>
              Anuluj
            </button>
            <button type="button" className="btn btn-primary" disabled={settingsSaving}
              onClick={saveSettings}>
              {settingsSaving ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </Modal>
      )}

      {showEditModal && (
        <Modal title="Edytuj maszynę" dismissable={!editSaving}
          onClose={() => setShowEditModal(false)}>
          <div className="form-group">
            <label className="form-label">Tytuł maszyny</label>
            <input className="form-input" value={editName} autoFocus
              placeholder="np. Prasa hydrauliczna nr 3"
              onChange={(e) => setEditName(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Opis (maks. 100 znaków)</label>
            <textarea className="form-input" value={editDescription} rows={3} maxLength={100}
              placeholder="Krótki opis maszyny"
              onChange={(e) => setEditDescription(e.target.value)} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" disabled={editSaving}
              onClick={() => setShowEditModal(false)}>
              Anuluj
            </button>
            <button type="button" className="btn btn-primary"
              disabled={editSaving || !editName.trim()}
              onClick={saveEdit}>
              {editSaving ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </Modal>
      )}

      <div className="detail-layout">
        <aside className="detail-aside">
          <div className="machine-thumbnail-box">
            {machine.thumbnailUrl ? (
              <img src={machine.thumbnailUrl} alt={machine.name} />
            ) : (
              <span className="machine-thumbnail-placeholder">Brak zdjęcia</span>
            )}
          </div>
          <div className="detail-info card">
            <div className="detail-info-title">Informacje</div>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Typ</span>
              <span className="info-value">{machineType?.name || '—'}</span>
            </div>
            {/* Klient patrzy na własną maszynę — zamiast danych jego firmy pokazujemy serwisanta. */}
            {isCustomer ? (
              <div className="info-item">
                <span className="info-label">Serwisuje</span>
                <span className="info-value">{machine.integratorName || '—'}</span>
              </div>
            ) : (
              <div className="info-item">
                <span className="info-label">Klient</span>
                <span className="info-value">
                  {customer?.companyName && (
                    <>
                      {customer.companyName}<br />
                    </>
                  )}
                  NIP {machine.ownerNip}
                </span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Dodał</span>
              <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {machine.createdByFirstName
                  ? `${machine.createdByFirstName} ${machine.createdByLastName}`
                  : '—'}
                {creatorBadge && <span className={creatorBadge.className}>{creatorBadge.label}</span>}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Data dodania</span>
              <span className="info-value">
                {machine.createdAt
                  ? new Date(machine.createdAt).toLocaleString('pl-PL', { dateStyle: 'long', timeStyle: 'short' })
                  : '—'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Opis</span>
              <span className="info-value">{machine.description || 'Brak opisu'}</span>
            </div>
          </div>
          </div>
        </aside>

        <div>
          <div className="customer-banner card" style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: '1 1 auto', minWidth: 0 }}>
              {customer?.logoUrl ? (
                <img
                  src={customer.logoUrl}
                  alt={`Logo ${customer.companyName || customer.nip}`}
                  style={{
                    width: 96, height: 96, flexShrink: 0,
                    objectFit: 'contain',
                    borderRadius: 8,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    padding: 6,
                  }}
                />
              ) : (
                <div style={{
                  width: 96, height: 96, flexShrink: 0,
                  borderRadius: 8,
                  border: '1px dashed var(--color-border)',
                  background: 'var(--color-surface)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', color: 'var(--color-text-muted)',
                }}>brak logo</div>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="customer-banner-label">Klient</div>
                {customer?.companyName && (
                  <div className="customer-banner-nip">{customer.companyName}</div>
                )}
                {customerAddress && (
                  <div className="customer-banner-address">
                    ul. {customerAddress.street} {customerAddress.buildingNumber}
                    {customerAddress.apartmentNumber && `/${customerAddress.apartmentNumber}`}
                    {', '}
                    {customerAddress.postalCode} {customerAddress.city}
                    {customerAddress.region && `, ${customerAddress.region}`}
                    {customerAddress.country && `, ${customerAddress.country}`}
                  </div>
                )}
                {!customer && (
                  <div className="customer-banner-address" style={{ fontStyle: 'italic' }}>
                    Klient o tym NIP-ie nie jest jeszcze zarejestrowany w systemie
                  </div>
                )}
              </div>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.4rem',
              flexShrink: 0,
            }}>
              {machine.ticketSystemEnabled && machine.qrSlug && (
                <>
                  <QRCodeSVG
                    id="mm-qr-banner-svg"
                    value={`${window.location.origin}/m/${machine.qrSlug}/report`}
                    size={120}
                    level="M"
                    includeMargin={false}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      printMachineQr(machine.name, machine.id, 'mm-qr-banner-svg');
                    }}
                    className="qr-print-btn"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="6 9 6 2 18 2 18 9" />
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Drukuj QR
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="machine-tabs-wrap">
            <div className={`tabs machine-tabs ${isCustomer ? 'machine-tabs--customer' : ''}`}>
              {tabs.map((t) => (
                <button
                  key={t.key}
                  data-tab={t.key}
                  className={`tab ${activeTab === t.key ? 'active' : ''}`}
                  onClick={() => switchTab(t.key)}
                >
                  <NavIcon name={TAB_ICON[t.key]} size={18} className="machine-tab-icon" />
                  <span className="machine-tab-label-full">{t.label}</span>
                  <span className="machine-tab-label-short">{TAB_SHORT[t.key]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="tab-content">
            {activeTab === 'notes' && (
          <div>
            <div className="tab-action-bar" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary" onClick={() => setShowNoteForm(true)}>
                Dodaj
              </button>
              {notes.length > 0 && (
                <button className="btn btn-danger" disabled={selectedNoteIds.size === 0}
                  onClick={() => setConfirmDelete('notes')}>
                  Usuń
                </button>
              )}
            </div>
            {showNoteForm && (
              <Modal title="Dodaj notatkę" dismissable={!savingNote}
                onClose={() => { setShowNoteForm(false); setNewNote(''); }}>
                <MessageEditor value={newNote} onChange={setNewNote} placeholder="Treść notatki..." rows={4} />
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" disabled={savingNote}
                    onClick={() => { setShowNoteForm(false); setNewNote(''); }}>
                    Anuluj
                  </button>
                  <button className="btn btn-primary" onClick={addNote} disabled={!newNote.trim() || savingNote}>
                    {savingNote ? 'Zapisuję…' : 'Zapisz notatkę'}
                  </button>
                </div>
              </Modal>
            )}
            {notes.length === 0 ? (
              <div className="empty-state">Brak notatek</div>
            ) : (
              <>
                <div className="items-list">
                  {notes.map((n) => (
                    <div key={n.id} className="item-card card" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      <input type="checkbox" checked={selectedNoteIds.has(n.id)}
                        onChange={() => toggleId(setSelectedNoteIds, n.id)}
                        style={{ width: 16, height: 16, marginTop: '0.2rem', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="item-desc"><Markdown content={n.description} /></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <span className="item-meta">{new Date(n.date).toLocaleString('pl-PL')}</span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            {isAdmin && n.embeddingPending && <span className="index-badge">Indeksowanie…</span>}
                            <CreatorLine firstName={n.createdByFirstName} lastName={n.createdByLastName} role={n.createdByRole} avatarUrl={n.createdByAvatarUrl} />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Pager page={notesPageNum} totalPages={notesTotalPages} onPage={(p) => loadNotes(p)} />
              </>
            )}
          </div>
        )}

        {activeTab === 'photos' && (
          <div>
            {isStaff && (
              <div className="tab-action-bar" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-primary" onClick={() => setShowPhotoForm(true)}>
                  Dodaj
                </button>
              </div>
            )}
            {showPhotoForm && (
              <Modal title="Dodaj zdjęcie" dismissable={!photoUploading}
                onClose={() => { setShowPhotoForm(false); resetPhotoForm(); }}>
                {photoError && <div className="auth-error">{photoError}</div>}
                <div className="form-group">
                  <label className="file-label">Wybierz zdjęcia (możesz zaznaczyć wiele)</label>
                  <input id="photo-file-input" type="file" className="form-input" accept="image/*" multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setPhotoFiles(files);
                      setPhotoError('');
                    }} />
                  {photoFiles.length > 0 && (
                    <ul className="photo-file-list">
                      {photoFiles.map((f, idx) => (
                        <li key={`${f.name}-${idx}`} className="photo-file-item">
                          <span className="photo-file-name" title={f.name}>{f.name}</span>
                          <span className="photo-file-size">{(f.size / 1024).toFixed(0)} KB</span>
                          <button
                            type="button"
                            className="photo-file-remove"
                            onClick={() => setPhotoFiles((prev) => prev.filter((_, i) => i !== idx))}
                            disabled={photoUploading}
                            aria-label={`Usuń ${f.name}`}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="form-group">
                  <input className="form-input"
                    placeholder={photoFiles.length > 1 ? 'Wspólny opis dla wszystkich zdjęć (opcjonalnie)' : 'Opis (opcjonalnie)'}
                    value={photoDescription}
                    onChange={(e) => setPhotoDescription(e.target.value)} />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowPhotoForm(false); resetPhotoForm(); }}
                    disabled={photoUploading}
                  >
                    Anuluj
                  </button>
                  <button className="btn btn-primary" onClick={addPhoto}
                    disabled={photoFiles.length === 0 || photoUploading}>
                    {photoUploading
                      ? (photoProgress ? `Wysyłanie ${photoProgress.done} / ${photoProgress.total}…` : 'Wysyłanie…')
                      : (photoFiles.length > 1 ? `Zapisz ${photoFiles.length} zdjęć` : 'Zapisz zdjęcie')}
                  </button>
                </div>
              </Modal>
            )}
            {photos.length === 0 ? (
              <div className="empty-state">Brak zdjęć</div>
            ) : (() => {
              const idx = Math.min(photoIndex, photos.length - 1);
              const p = photos[idx];
              const isThumb = machine.thumbnailPhotoId === p.id;
              const prev = () => setPhotoIndex((i) => (i - 1 + photos.length) % photos.length);
              const next = () => setPhotoIndex((i) => (i + 1) % photos.length);
              return (
                <div className="photo-carousel card">
                  <div className="photo-carousel-stage">
                    <button
                      type="button"
                      className="photo-nav photo-nav-prev"
                      onClick={prev}
                      disabled={photos.length < 2}
                      aria-label="Poprzednie zdjęcie"
                    >
                      &lsaquo;
                    </button>
                    <div className="photo-carousel-imgwrap">
                      {isThumb && (
                        <span className="photo-thumb-badge">★ Miniaturka</span>
                      )}
                      <img
                        src={p.filePath}
                        alt={p.description || 'Zdjęcie maszyny'}
                        className={`photo-carousel-img ${isThumb ? 'is-thumbnail' : ''}`}
                      />
                    </div>
                    <button
                      type="button"
                      className="photo-nav photo-nav-next"
                      onClick={next}
                      disabled={photos.length < 2}
                      aria-label="Następne zdjęcie"
                    >
                      &rsaquo;
                    </button>
                  </div>
                  <div className="photo-carousel-counter">
                    {idx + 1} / {photos.length}
                  </div>
                  <p className="item-desc" style={{ marginTop: '0.75rem' }}>
                    {p.description || 'Brak opisu'}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="item-meta">{new Date(p.date).toLocaleString('pl-PL')}</span>
                    <CreatorLine firstName={p.createdByFirstName} lastName={p.createdByLastName} role={p.createdByRole} avatarUrl={p.createdByAvatarUrl} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem', alignItems: 'center', justifyContent: 'center' }}>
                    {isThumb ? (
                      <button className="btn btn-primary btn-sm" onClick={async () => {
                        try {
                          const { data } = await machineApi.clearThumbnail(machineId);
                          setMachine(data);
                          showToast('success', 'Usunięto jako miniaturkę.');
                        } catch (e) {
                          console.error('Nie udało się zmienić miniatury', e);
                          showToast('error', 'Nie udało się zmienić miniatury. Spróbuj ponownie.');
                        }
                      }}>
                        Usuń jako miniaturkę
                      </button>
                    ) : (
                      <button className="btn btn-primary btn-sm" onClick={async () => {
                        try {
                          const { data } = await machineApi.setThumbnail(machineId, p.id);
                          setMachine(data);
                          showToast('success', 'Ustawiono jako miniaturkę.');
                        } catch (e) {
                          console.error('Nie udało się zmienić miniatury', e);
                          showToast('error', 'Nie udało się zmienić miniatury. Spróbuj ponownie.');
                        }
                      }}>
                        Ustaw jako miniaturkę
                      </button>
                    )}
                    {isStaff && (
                    <button className="btn btn-danger btn-sm"
                      onClick={() => setConfirmDeletePhotoId(p.id)}>
                      Usuń zdjęcie
                    </button>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'docs' && (
          <div>
            {isStaff && (
              <div className="tab-action-bar" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-primary" onClick={() => setShowDocForm(true)}>
                  Dodaj
                </button>
              </div>
            )}
            {showDocForm && (
              <Modal title="Dodaj dokumentację" dismissable={!docUploading}
                onClose={() => { setShowDocForm(false); resetDocForm(); }}>
                {docError && <div className="auth-error">{docError}</div>}
                {docDuplicate && (
                  <div className="card" style={{
                    padding: '0.75rem 1rem', marginBottom: '1rem',
                    borderLeft: '4px solid var(--color-primary)',
                  }}>
                    <p className="item-desc" style={{ marginBottom: '0.35rem' }}>{docDuplicate.message}</p>
                    {docDuplicate.machines.length > 0 && (
                      <p className="item-meta">
                        Obecnie podpięty do: {docDuplicate.machines.map((m) => m.name).join(', ')}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                      {!docDuplicate.alreadyLinkedToThisMachine && (
                        <button type="button" className="btn btn-primary" onClick={linkDuplicateDoc}>
                          Podepnij „{docDuplicate.name}”
                        </button>
                      )}
                      <button type="button" className="btn btn-secondary" onClick={() => setDocDuplicate(null)}>
                        {docDuplicate.alreadyLinkedToThisMachine ? 'OK' : 'Wybiorę inny plik'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Nazwa dokumentu</label>
                  <input className="form-input" placeholder="np. Instrukcja obsługi" value={docName}
                    onChange={(e) => setDocName(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Opis dokumentu (opcjonalnie)</label>
                  <textarea className="form-input" placeholder="Krótki opis zawartości, wersja, itd."
                    value={docDescription} rows={3}
                    onChange={(e) => setDocDescription(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Plik dokumentu</label>
                  <input id="doc-file-input" type="file" className="form-input"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" disabled={docUploading}
                    onClick={() => { setShowDocForm(false); resetDocForm(); }}>
                    Anuluj
                  </button>
                  <button type="button" className="btn btn-primary" disabled={docUploading}
                    onClick={() => { setShowDocForm(false); setShowAttachModal(true); }}>
                    Podepnij istniejący
                  </button>
                  <button className="btn btn-primary" onClick={addDoc} disabled={!docFile || docUploading}>
                    {docUploading ? 'Wysyłanie...' : 'Zapisz dokument'}
                  </button>
                </div>
              </Modal>
            )}
            {docs.length === 0 ? (
              <div className="empty-state">Brak dokumentacji</div>
            ) : (
              <div className="items-list">
                {docs.map((d) => (
                  <div key={d.id} className="item-card card"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="item-desc"><strong>{d.name}</strong></p>
                      {d.description && <p className="item-meta">{d.description}</p>}
                      <p className="item-meta">
                        Gotowa do odpytywania:{' '}
                        <strong style={{ color: d.indexed ? '#2e9956' : '#cf3a32' }}>
                          {d.indexed ? 'Tak' : 'Nie'}
                        </strong>
                      </p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.6rem', flexShrink: 0 }}>
                      <CreatorLine firstName={d.createdByFirstName} lastName={d.createdByLastName}
                        role={d.createdByRole} avatarUrl={d.createdByAvatarUrl} />
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '0.5rem', minWidth: 180 }}>
                        {isStaff && (
                          <button className="btn btn-primary" onClick={() => unlinkDoc(d.id)}>
                            Odepnij od maszyny
                          </button>
                        )}
                        <DownloadButton href={documentationDownloadUrl(d.id)} style={{ width: '100%' }} />
                        {isAdmin && (
                          <button className="btn btn-danger" onClick={() => deleteDoc(d.id)}>
                            Usuń całkowicie
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showAttachModal && (
              <AttachDocumentationModal
                machineId={machineId}
                linkedIds={docs.map((d) => d.id)}
                onClose={() => setShowAttachModal(false)}
                onAttached={() => { setShowAttachModal(false); loadTabData(); }}
              />
            )}
          </div>
        )}

        {activeTab === 'tickets' && machine.ticketSystemEnabled && <TicketsTab machineId={machineId} />}

        {activeTab === 'maintenance' && (
          <div>
            {!isCustomer && (
              <div className="tab-action-bar" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-primary" onClick={() => setShowMaintForm(true)}>
                  Dodaj
                </button>
                {maintenance.length > 0 && (
                  <button className="btn btn-danger" disabled={selectedMaintIds.size === 0}
                    onClick={() => setConfirmDelete('maint')}>
                    Usuń
                  </button>
                )}
              </div>
            )}
            {showMaintForm && (
              <Modal title="Dodaj czynność serwisową" dismissable={!savingMaint}
                onClose={() => { setShowMaintForm(false); setMaintForm({ description: '' }); }}>
                <MessageEditor
                  value={maintForm.description}
                  onChange={(v) => setMaintForm({ description: v })}
                  placeholder="Opis czynności serwisowej..."
                  rows={4}
                />
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" disabled={savingMaint}
                    onClick={() => { setShowMaintForm(false); setMaintForm({ description: '' }); }}>
                    Anuluj
                  </button>
                  <button className="btn btn-primary" onClick={addMaintenance} disabled={!maintForm.description.trim() || savingMaint}>
                    {savingMaint ? 'Zapisuję…' : 'Zapisz czynność'}
                  </button>
                </div>
              </Modal>
            )}
            {maintenance.length === 0 ? (
              <div className="empty-state">Brak czynności serwisowych</div>
            ) : (
              <>
                <div className="items-list">
                  {maintenance.map((m) => (
                    <div key={m.id} className="item-card card" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                      {!isCustomer && (
                        <input type="checkbox" checked={selectedMaintIds.has(m.id)}
                          onChange={() => toggleId(setSelectedMaintIds, m.id)}
                          style={{ width: 16, height: 16, marginTop: '0.2rem', flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="item-desc"><Markdown content={m.description} /></div>
                        <span className="item-meta">{new Date(m.date).toLocaleString('pl-PL')}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <Pager page={maintPageNum} totalPages={maintTotalPages} onPage={(p) => loadMaint(p)} />
              </>
            )}
          </div>
        )}
          </div>
        </div>
      </div>

      {confirmDelete && (
        <Modal title="Usuń zaznaczone" dismissable={!deleting} maxWidth={440} onClose={() => setConfirmDelete(null)}>
          <p style={{ marginBottom: '0.75rem' }}>
            Usunąć {confirmDelete === 'notes' ? selectedNoteIds.size : selectedMaintIds.size}{' '}
            {confirmDelete === 'notes' ? 'zaznaczonych notatek' : 'zaznaczonych czynności'}? Tej operacji nie można cofnąć.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(null)} disabled={deleting}>
              Anuluj
            </button>
            <button type="button" className="btn btn-danger" onClick={runDelete} disabled={deleting}>
              {deleting ? 'Usuwam…' : 'Potwierdź'}
            </button>
          </div>
        </Modal>
      )}

      {confirmDeletePhotoId && (
        <Modal title="Usuń zdjęcie" dismissable={!deletingPhoto} maxWidth={440}
          onClose={() => setConfirmDeletePhotoId(null)}>
          <p style={{ marginBottom: '0.75rem' }}>
            Usunąć to zdjęcie? Tej operacji nie można cofnąć.
          </p>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmDeletePhotoId(null)} disabled={deletingPhoto}>
              Anuluj
            </button>
            <button type="button" className="btn btn-danger" onClick={deletePhoto} disabled={deletingPhoto}>
              {deletingPhoto ? 'Usuwam…' : 'Usuń'}
            </button>
          </div>
        </Modal>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

interface AttachDocumentationModalProps {
  machineId: string;
  linkedIds: string[];
  onClose: () => void;
  onAttached: () => void;
}

const AttachDocumentationModal: React.FC<AttachDocumentationModalProps> = ({
  machineId, linkedIds, onClose, onAttached,
}) => {
  const [docs, setDocs] = useState<DocumentationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [linking, setLinking] = useState<string | null>(null);

  useEffect(() => {
    // Picker podpinania — potrzebujemy pełnej listy, więc bierzemy dużą stronę.
    documentationApi.listAll({ page: 0, size: 1000 })
      .then((r) => setDocs(r.data.content))
      .finally(() => setLoading(false));
  }, []);

  const attach = async (id: string) => {
    setLinking(id);
    try {
      await documentationApi.link(machineId, id);
      onAttached();
    } finally {
      setLinking(null);
    }
  };

  const linkedSet = new Set<string>(linkedIds);
  const available = docs.filter((d) => !linkedSet.has(d.id));
  const filtered = search.trim()
    ? available.filter((d) =>
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        (d.description || '').toLowerCase().includes(search.toLowerCase()))
    : available;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <h2 className="modal-title">Podepnij istniejący dokument</h2>
        <div className="form-group">
          <input className="form-input" placeholder="Szukaj po nazwie lub opisie..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {loading ? (
          <div className="empty-state">Ładowanie...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            {available.length === 0 ? 'Brak innych dokumentów do podpięcia' : 'Brak wyników'}
          </div>
        ) : (
          <div className="items-list" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {filtered.map((d) => (
              <div key={d.id} className="item-card card">
                <p className="item-desc"><strong>{d.name}</strong></p>
                {d.description && <p className="item-meta">{d.description}</p>}
                <p className="item-meta">
                  Podpięty do {d.linkedMachineIds.length} maszyn{d.linkedMachineIds.length === 1 ? 'y' : ''}
                </p>
                <button className="btn btn-primary btn-sm" style={{ marginTop: '0.5rem' }}
                  disabled={linking === d.id} onClick={() => attach(d.id)}>
                  {linking === d.id ? 'Podpinam...' : 'Podepnij'}
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Zamknij</button>
        </div>
      </div>
    </div>
  );
};

export default MachineDetail;
